import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import AsideHeader from '../AsideHeader';
import { animated, useSpring } from 'react-spring';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Heading5, Variant } from '~/components/atom/Typography';
import TEST_ID from './index.testid';
import useMeasure from '~/hooks/useMeasure';
import ColorInput from '~/components/molecule/ColorInput';
import type {
  WidgetSettingsStyleConfigColorPair,
  WidgetSettingsStyles,
} from '~/graphql/types';
import { themeState as themeStateAtom } from '../../../../state/theme';
import { layoutState as layoutStateAtom } from '../../../../state/layout';
import BrandSettingsCTA from '~/components/organism/BrandSettingsCTA';
import JustificationContainer from '~/components/atom/JustificationContainer';
import ImageOrAssetUploadComponent from '~/components/molecule/ImageOrAssetUploadComponent';
import { initialFormAtom } from '../../../../state';

type Props = {
  dataTestId?: string;
  expanded?: boolean;
  onToggleExpand: () => void;
};

const text = {
  brandSettingsCTAHeader:
    'Pas de instellingen van je huisstijl toe op dit formulier',
  heading: 'Ontwerp',
  colors: {
    primary: {
      color: 'Primaire tekstkleur',
      background: 'Primaire kleur',
    },
    secondary: {
      color: 'Secondaire tekstkleur',
      background: 'Secondaire kleur',
    },
  },
  backgroundColor: 'Achtergrond kleur',
  backgroundImage: 'Achtergrond afbeelding',
};

const DesignSettings: React.FCC<Props> = ({
  dataTestId,
  expanded = false,
  onToggleExpand,
}) => {
  const [themeState, setThemeState] = useRecoilState(themeStateAtom);
  const [layoutState, setLayoutState] = useRecoilState(layoutStateAtom);
  const initialFormValue = useRecoilValue(initialFormAtom);

  const { ref, bounds } = useMeasure();
  const theme = useTheme();

  const spring = useSpring({
    height: expanded ? bounds.height + theme.remToPxRaw(theme.space('m')) : 0,
  });

  const onColorChange = ({
    variant,
    key,
    value,
  }: {
    variant: keyof Omit<WidgetSettingsStyles, '__typename'>;
    key: keyof Omit<WidgetSettingsStyleConfigColorPair, '__typename'>;
    value: string;
  }) =>
    setThemeState(prev =>
      prev
        ? {
            ...prev,
            colors: {
              ...prev.colors,
              [variant]: {
                ...prev.colors[variant],
                [key]: value,
              },
            },
          }
        : prev,
    );

  return (
    <Container data-testid={dataTestId}>
      <AsideHeader
        dataTestId={TEST_ID.ASIDE_HEADER}
        heading={text.heading}
        icon="layout"
        onClick={onToggleExpand}
        expanded={expanded}
      />
      <OverflowContainer style={spring}>
        <JustificationContainer
          direction="column"
          gap="xs"
          padding={[null, 'm']}
          width="100%"
          ref={ref}
        >
          <BrandSettingsCTA
            header={text.brandSettingsCTAHeader}
            subjects={['colors']}
            onConfirm={values => {
              if (!values.colors) return;
              const { primary, secondary } = values.colors;

              setThemeState(prev => ({
                ...prev,
                colors: {
                  ...prev.colors,
                  primary: {
                    ...prev.colors.primary,
                    ...primary,
                  },
                  secondary: {
                    ...prev.colors.secondary,
                    ...secondary,
                  },
                },
              }));
            }}
          />
          <InputContainer>
            <Heading5 variant={Variant.primary}>
              {text.colors.primary.background}
            </Heading5>
            <ColorInput
              value={themeState.colors.primary.background}
              onChange={color =>
                onColorChange({
                  variant: 'primary',
                  key: 'background',
                  value: color,
                })
              }
            />
          </InputContainer>

          <InputContainer>
            <Heading5 variant={Variant.primary}>
              {text.colors.primary.color}
            </Heading5>
            <ColorInput
              value={themeState.colors.primary.color}
              onChange={color =>
                onColorChange({
                  variant: 'primary',
                  key: 'color',
                  value: color,
                })
              }
            />
          </InputContainer>

          <InputContainer>
            <Heading5 variant={Variant.primary}>
              {text.colors.secondary.background}
            </Heading5>
            <ColorInput
              value={themeState.colors.secondary.background}
              onChange={color =>
                onColorChange({
                  variant: 'secondary',
                  key: 'background',
                  value: color,
                })
              }
            />
          </InputContainer>

          <InputContainer>
            <Heading5 variant={Variant.primary}>
              {text.colors.secondary.color}
            </Heading5>
            <ColorInput
              value={themeState.colors.secondary.color}
              onChange={color =>
                onColorChange({
                  variant: 'secondary',
                  key: 'color',
                  value: color,
                })
              }
            />
          </InputContainer>

          <InputContainer>
            <InputContainer>
              <Heading5 variant={Variant.primary}>
                {text.backgroundColor}
              </Heading5>
              <ColorInput
                value={layoutState.backgroundColor}
                onChange={color => {
                  setLayoutState(prev =>
                    prev ? { ...prev, backgroundColor: color } : prev,
                  );
                }}
              />
            </InputContainer>

            <Heading5 variant={Variant.primary}>
              {text.backgroundImage}
            </Heading5>
            <ImageOrAssetUploadComponent
              allowDeletion
              imageFileName="default_layout_background_image"
              containerDirection="column"
              currentImage={layoutState.backgroundImage}
              initialImage={initialFormValue?.style?.layout?.backgroundImage}
              onParentUpdate={args => {
                setLayoutState(prev =>
                  prev
                    ? {
                        ...prev,
                        backgroundImage: args,
                      }
                    : prev,
                );
              }}
            />
          </InputContainer>
        </JustificationContainer>
      </OverflowContainer>
    </Container>
  );
};

const Container = styled.aside(
  ({ theme }) => css`
    z-index: ${theme.z('top')};
    user-select: none;
    width: 100%;
  `,
);

const InputContainer = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.space('xxs')};
    width: 100%;
  `,
);

const OverflowContainer = styled(animated.div)`
  overflow-x: hidden;
`;

export default DesignSettings;
